import { AfterViewInit, Component, Inject, OnDestroy, OnInit, Optional, Self } from '@angular/core';
import { FormBuilder, NgControl, Validators  } from '@angular/forms';
import { IAngularMyDpOptions } from '@nodro7/angular-mydatepicker';
import { Subscription } from 'rxjs';
import { APP_CONFIG } from '../../../app.config';
import { AssistantPopoverService } from '../../../common/assistant-popover.service';
import { DateUtilsService } from '../../../common/date-utils.service';
import { DATEPICKER_CONFIG } from '../../../common/datepicker-config';
import { FixDateService } from '../../../common/fixDate.service';
import { ValidationService } from '../../../common/validation.service';
import { ValueListsService } from '../../../common/value-lists.service';
import { AppConfig } from '../../../models/config/app-config';
import { ValueListItem } from '../../../models/rest/valuelist/valuelist';
import { VisumErfassungSubFormComponent } from '../visum-erfassung-sub-form/visum-erfassung-sub-form.component';
import { Subject } from 'rxjs';
import { PassRestrictedCheckboxTextKey } from '../../../shared/components/pass-restricted-checkbox/pass-restricted-checkbox-texts/pass-restricted-checkbox-texts.component';

@Component({
  selector: 'app-kontaktdaten',
  templateUrl: './kontaktdaten.component.html',
  styleUrls: ['./kontaktdaten.component.scss']
})
export class KontaktdatenComponent extends VisumErfassungSubFormComponent implements OnInit, OnDestroy, AfterViewInit {
  private wohnsitzAnderesLandSub: Subscription;
  private landListItemsSub: Subscription;

  landListItems: ValueListItem[];

  readonly wohnsitzAnderesLandSelected$ = new Subject<string | null>();

  get isCVisum(): boolean {
    return this.appConfig.visumAntragsartEnum === 'KURZFRISTIGER_AUFENTHALT';
  }

  get isDVisum(): boolean {
    return !this.isCVisum;
  }


  readonly wohnsitzTitleKey: PassRestrictedCheckboxTextKey = 'KONTAKTDATEN_WOHNSITZ_TITLE';
  readonly wohnsitzLabels: Record<string, PassRestrictedCheckboxTextKey> = {
    YES: 'KONTAKTDATEN_WOHNSITZ_YES',
    NO: 'KONTAKTDATEN_WOHNSITZ_NO'
  };

  constructor(fb: FormBuilder,
              private valueListsService: ValueListsService,
              private validationService: ValidationService,
              @Inject(DATEPICKER_CONFIG) public datepickerOptions: IAngularMyDpOptions,
              fixDateService: FixDateService,
              dateUtils: DateUtilsService,
              @Optional() @Self() controlDir: NgControl,
              assistantPopoverService: AssistantPopoverService,
              @Inject(APP_CONFIG) public appConfig: AppConfig) {
    super(controlDir, assistantPopoverService);


    this.form = this.wrapFormGroup(fb.group({
      anschriftStrasse: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.strasse')],
      anschriftHausnummer: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.hausnummer')],
      anschriftSonstiges: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.sonstigeAdressangaben')],
      anschriftPLZ: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.plz')],
      anschriftOrt: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.ort')],
      anschriftLand: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.land')],
      anschriftTelefon: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.kontaktdaten.telefon')],
      anschriftEmail: [null, validationService.getValidators('antragsteller.personendaten.staendigeAnschrift.kontaktdaten.email', false, true)],
      aufenthaltsberechtigungArt: [null, validationService.getValidators('antragsteller.aufenthaltsberechtigung.artDerRueckkehrberechtigung')],
      aufenthaltsberechtigungNummer: [null, validationService.getValidators('antragsteller.aufenthaltsberechtigung.rueckkehrDokumentNr')],
      aufenthaltsberechtigungGueltigkeitbis: [null, validationService.getValidators('antragsteller.aufenthaltsberechtigung.rueckkehrGueltigBis', true)],
      wohnsitzAnderesLand: [null, []],
      wohnsitzAnderesLandAuswahl: [
        null,
        [
          Validators.required,
          ...this.validationService.getValidators('antragsteller.aufenthaltsberechtigung.wohnsitzAnderesLandAuswahl')
        ]
      ]
    }));

    if (this.isCVisum) {
      this.form.controls.wohnsitzAnderesLandAuswahl.setValidators([
        Validators.required,
        ...this.validationService.getValidators('antragsteller.aufenthaltsberechtigung.wohnsitzAnderesLandAuswahl')
      ]);
      this.form.controls.wohnsitzAnderesLand.setValidators([]); // optional (nur wenn D vorher required war)
    } else {
      this.form.controls.wohnsitzAnderesLandAuswahl.clearValidators();
      this.form.controls.wohnsitzAnderesLandAuswahl.setValue(null, { emitEvent: false });
      this.form.controls.wohnsitzAnderesLand.setValidators([Validators.required]);
    }

    this.form.controls.wohnsitzAnderesLandAuswahl.updateValueAndValidity({ emitEvent: false });
    this.form.controls.wohnsitzAnderesLand.updateValueAndValidity({ emitEvent: false });


    this.dateFixer = fixDateService.getConverter({ unknownReplacement: '00' });
    this.dateFormatter = dateUtils.getFormatter('yMd');
  }

  ngOnInit() {
    super.ngOnInit();

    this.landListItemsSub = this.valueListsService.getValueListItems('land')
    .subscribe((listItems: ValueListItem[]) => this.landListItems = listItems);

    this.wohnsitzAnderesLandSub = (this.isCVisum
        ? this.form.controls.wohnsitzAnderesLandAuswahl.valueChanges
        : this.form.controls.wohnsitzAnderesLand.valueChanges
    ).subscribe((val: any) => {
      if (this.isCVisum) {
        const key = val as (string | null);
        const boolVal = key === 'YES' ? true : key === 'NO' ? false : null;
        this.form.controls.wohnsitzAnderesLand.setValue(boolVal, { emitEvent: false });
        this.enableOrDisableFields(boolVal === true);
      } else {
        const boolVal = val as (boolean | null);
        this.enableOrDisableFields(boolVal === true);
      }
    });

  }

  ngAfterViewInit() {
    super.ngAfterViewInit();
  }

  ngOnDestroy(): void {
    this.wohnsitzAnderesLandSub.unsubscribe();
    this.landListItemsSub.unsubscribe();
  }

  onValidatorUpdate() {
    super.onValidatorUpdate();
    if (this.isCVisum) {
      const key = this.form.controls.wohnsitzAnderesLandAuswahl.value as (string | null);
      this.wohnsitzAnderesLandSelected$.next(key ?? null);
      this.enableOrDisableFields(key === 'YES');
    } else {
      const boolVal = this.form.controls.wohnsitzAnderesLand.value as (boolean | null);
      this.enableOrDisableFields(boolVal === true);
    }
  }


  private enableOrDisableFields(enabled: boolean) {
    this.enableOrDisableControlWithTimeout(enabled, this.form.controls.aufenthaltsberechtigungArt);
    this.enableOrDisableControlWithTimeout(enabled, this.form.controls.aufenthaltsberechtigungNummer);
    this.enableOrDisableControlWithTimeout(enabled, this.form.controls.aufenthaltsberechtigungGueltigkeitbis);
  }
}
